import { isNil, pickBy } from 'lodash';

import { FlowType } from '@jerry-serverless/types';

import type { AppData } from '../../types/app_data';
import { isGarageFlow } from '../garage';

import type { AnalyticsUser } from './types';

/**
 * Set user data for Google.
 * https://support.google.com/google-ads/answer/13258081?hl=en#zippy=%2Cidentify-and-define-your-enhanced-conversions-fields
 */
const setUserData = (userData?: { phone?: string; email?: string | null; enableLDU?: boolean }): void => {
  if (window.gtag) {
    window.gtag(
      'set',
      pickBy(
        {
          phone_number: userData?.phone ? `+1${userData?.phone}` : undefined,
          email: userData?.email?.toLowerCase?.() || undefined,
          restricted_data_processing: userData?.enableLDU ? true : undefined,
        },
        (value) => !isNil(value)
      ) as Record<string, string | boolean>
    );
  }
};

/**
 * Identify user to Google (Analytics and Tag).
 *
 * @param user
 */
export const setUserForGoogle = async (user: AnalyticsUser): Promise<void> => {
  if (window.gtag) {
    if (window.gaID) {
      window.gtag('config', window.gaID, { user_id: `${user.id}`, allow_enhanced_conversions: true });
    }
    if (window.gtagID) {
      window.gtag('config', window.gtagID, { user_id: `${user.id}`, allow_enhanced_conversions: true });
    }
    if (window.gawConversionID) {
      window.gtag('config', window.gawConversionID, { user_id: `${user.id}`, allow_enhanced_conversions: true });
    }
  }

  setUserData(user);
};

/**
 * Track signup conversion.
 */
export const trackSignedUpForGoogle = (appData?: AppData): void => {
  setUserData(appData);

  if (window.gtag) {
    window.gtag('event', 'Signup', 'Phone Code & Authorize');

    // NOTE: What are these for? - M.S.
    window.gtag('event', 'sign_up', { method: window.ReactNativeWebView ? 'app' : 'web' });
    if (window.gawConversionID && window.gawSignupConversionLabel) {
      window.gtag('event', 'conversion', {
        send_to: `${window.gawConversionID}/${window.gawSignupConversionLabel}`,
      });
    }
  }
};

/**
 * Track onboarding conversion.
 */
export const trackOnboardedForGoogle = (appData?: AppData): void => {
  setUserData(appData);

  if (window.gtag) {
    window.gtag('event', 'onboarding_complete', { method: window.ReactNativeWebView ? 'app' : 'web' });
  }

  if (window.gtag && appData?.flowType && FlowType.CarValue === appData?.flowType) {
    window.gtag('event', 'garage_signup_car_value');
  }

  if (window.gtag && appData?.flowType && FlowType.CarRecall === appData?.flowType) {
    window.gtag('event', 'garage_signup_car_recall');
  }

  if (window.gtag && appData?.flowType && FlowType.Maintenance === appData?.flowType) {
    window.gtag('event', 'garage_signup_maintenance');
  }

  if (window.gtag && appData?.flowType && FlowType.RepairShop === appData?.flowType) {
    window.gtag('event', 'garage_signup_repair_shop');
  }

  if (window.gtag && appData && isGarageFlow(appData)) {
    window.gtag('event', 'garage_signup');
  }
};
